/**
 * =============================================================================
 * MyActivitiesKpiCard — Card KPI premium dos dashboards de Atividades
 *
 * Visual padronizado com o dashboard de Tarefas (CrmPremiumKpiCard):
 *  - Anel SVG de progresso circular ao redor do ícone (esquerda)
 *  - Valor grande em destaque + label compacta (direita)
 *  - Sombra premium, hover com leve scale e elevação
 *  - Suporte a clique (drill-down), tooltip via `hint`, e delta/trend opcional
 *
 * IMPORTANTE: A API pública (props) é mantida 100% compatível com a versão
 * anterior, para que nenhum dos pontos de uso precise ser alterado.
 *
 * RELEASE:
 *   2026-05-14 v1.0.0 - Versão inicial (label/ícone/valor)
 *   2026-05-15 v2.0.0 - Refator visual para padrão CrmPremiumKpiCard (anel + ícone)
 * =============================================================================
 */
import React from 'react';
import {
  LucideIcon,
  TrendingDown,
  TrendingUp,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

// Tonalidades suportadas (mantidas para retrocompatibilidade)
export type MyKpiTone = 'teal' | 'sky' | 'rose' | 'amber' | 'violet' | 'lime';

// Mapeamento: tone → cor do anel + cor do ícone + glow do hover
const TONE_STYLES: Record<
  MyKpiTone,
  { ringColor: string; iconColor: string; bgGlow: string }
> = {
  teal: {
    ringColor: '#10b981', // emerald-500
    iconColor: 'text-emerald-600',
    bgGlow: 'hover:shadow-emerald-100/50 dark:hover:shadow-emerald-900/20',
  },
  sky: {
    ringColor: '#3b82f6', // blue-500
    iconColor: 'text-blue-600',
    bgGlow: 'hover:shadow-blue-100/50 dark:hover:shadow-blue-900/20',
  },
  rose: {
    ringColor: '#ef4444', // red-500
    iconColor: 'text-rose-600',
    bgGlow: 'hover:shadow-rose-100/50 dark:hover:shadow-rose-900/20',
  },
  amber: {
    ringColor: '#f59e0b', // amber-500
    iconColor: 'text-amber-600',
    bgGlow: 'hover:shadow-amber-100/50 dark:hover:shadow-amber-900/20',
  },
  violet: {
    ringColor: '#8b5cf6', // violet-500
    iconColor: 'text-violet-600',
    bgGlow: 'hover:shadow-violet-100/50 dark:hover:shadow-violet-900/20',
  },
  lime: {
    ringColor: '#84cc16', // lime-500
    iconColor: 'text-lime-600',
    bgGlow: 'hover:shadow-lime-100/50 dark:hover:shadow-lime-900/20',
  },
};

interface Props {
  /** Texto curto (ex: "Pendentes hoje"). */
  label: string;
  /** Valor principal (string ou número). */
  value: React.ReactNode;
  /** Ícone Lucide centralizado no anel. */
  icon: LucideIcon;
  /** Tonalidade visual do anel/ícone. */
  tone?: MyKpiTone;
  /** Texto explicativo — exibido como tooltip (?) ao lado da label. */
  hint?: string;
  /** Indicador delta (ex: "+12%", "atenção"). Renderizado em pílula abaixo. */
  delta?: string;
  /** Direção do delta: 'up' verde, 'down' vermelho, 'neutral' cinza. */
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
  /** Quando definido, torna o card clicável (drill-down). */
  onClick?: () => void;
}

/**
 * Componente interno: anel de progresso SVG (idêntico ao CrmPremiumKpiCard).
 */
function ProgressRing({
  progress,
  color,
  size = 36,
  strokeWidth = 2.5,
}: {
  progress: number;
  color: string;
  size?: number;
  strokeWidth?: number;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="transform -rotate-90"
    >
      {/* Anel de fundo (cinza suave) */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-muted/20"
      />
      {/* Anel de progresso (colorido) */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
}

/**
 * Heurística para calcular o progresso do anel a partir do valor:
 *  - Se for percentual (string terminando em %), usa o número.
 *  - Caso contrário, retorna 75 (mesmo padrão visual usado em TaskKpiCards).
 */
function computeProgress(value: React.ReactNode): number {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.endsWith('%')) {
      const n = parseFloat(trimmed.replace(',', '.'));
      if (!Number.isNaN(n)) return Math.min(100, Math.max(0, n));
    }
  }
  return 75;
}

/**
 * Card KPI premium reutilizável — visual alinhado ao dashboard de Tarefas.
 */
export function MyActivitiesKpiCard({
  label,
  value,
  icon: Icon,
  tone = 'teal',
  hint,
  delta,
  trend = 'neutral',
  className,
  onClick,
}: Props) {
  const styles = TONE_STYLES[tone];
  const progress = computeProgress(value);
  const isClickable = typeof onClick === 'function';

  const TrendIcon = trend === 'down' ? TrendingDown : TrendingUp;
  const trendColor =
    trend === 'up'
      ? 'text-emerald-600 dark:text-emerald-400'
      : trend === 'down'
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-muted-foreground';

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        // Base — card premium compacto
        'relative bg-card rounded-xl p-3 h-full flex flex-col justify-center',
        // Sombras premium com transição suave
        'shadow-lg hover:shadow-xl',
        styles.bgGlow,
        // Borda sutil
        'border border-border/50',
        // Animação hover
        'transition-all duration-300 ease-out',
        'sm:hover:scale-[1.02] sm:hover:-translate-y-0.5',
        // Cursor / acessibilidade
        isClickable
          ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
          : 'cursor-default',
        className,
      )}
    >
      <div className="flex items-center justify-center gap-2 h-full">
        {/* Anel + ícone */}
        <div className="relative flex-shrink-0">
          <ProgressRing
            progress={progress}
            color={styles.ringColor}
            size={36}
            strokeWidth={2.5}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className={cn('h-3 w-3', styles.iconColor)} />
          </div>
        </div>

        {/* Texto: valor + label (+ tooltip via hint) (+ delta opcional) */}
        <div className="flex-1 min-w-0">
          <p className="text-xl font-bold text-foreground truncate leading-tight">
            {value}
          </p>

          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground truncate">{label}</p>
            {hint && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground/60 hover:text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                    {hint}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Delta/trend opcional — pílula compacta */}
          {delta && (
            <div
              className={cn(
                'mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold',
                trendColor,
              )}
            >
              <TrendIcon className="h-3 w-3" />
              {delta}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
